// Pure WebRTC plumbing for the mobile-camera-as-webcam feature. No React.
//
// One side plays "desktop" (recvonly), the other "mobile" (sendonly with
// camera + mic). Signaling is decoupled — the caller wires offer/answer/ICE
// through whatever transport they want (we use Firebase RTDB elsewhere).
//
// We use only Google's public STUN. No TURN — by design, peers that can't
// reach each other directly (different NATs, mobile data) will fail to
// connect, which enforces the "same WiFi" invariant the feature requires.

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const PC_CONFIG = {
  iceServers: ICE_SERVERS,
  iceCandidatePoolSize: 4,
};

// ── Desktop side (caller, recvonly) ─────────────────────────────────────
// Creates the offer, accepts the answer, surfaces the inbound stream via
// onTrack callback. Returns a controller for shutdown.
export const createDesktopHost = ({
  signaling,
  onTrack,
  onConnectionStateChange,
}) => {
  const pc = new RTCPeerConnection(PC_CONFIG);

  // Desktop only consumes — we add transceivers in recvonly direction so
  // the SDP includes both video + audio media sections without us having to
  // capture local media.
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  pc.onicecandidate = (e) => {
    if (e.candidate) {
      try { signaling.sendDesktopIce(e.candidate.toJSON()); } catch (err) { /* noop */ }
    }
  };

  pc.ontrack = (e) => {
    if (typeof onTrack === 'function' && e.streams && e.streams[0]) {
      onTrack(e.streams[0]);
    }
  };

  // Track whether ICE has ever reached the connected state so we don't bail
  // on a transient `disconnected` flap before we've even handshaken.
  let everConnected = false;
  let restartTimer = null;
  const renegotiate = async () => {
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      await signaling.writeOffer({ sdp: pc.localDescription.sdp, type: pc.localDescription.type });
    } catch (e) { /* noop */ }
  };

  pc.onconnectionstatechange = () => {
    const state = pc.connectionState;
    if (state === 'connected') {
      everConnected = true;
      if (restartTimer) { clearTimeout(restartTimer); restartTimer = null; }
    }
    if (typeof onConnectionStateChange === 'function') {
      onConnectionStateChange(state);
    }
    // `disconnected` is often transient — give the browser ~4s to recover
    // on its own (WiFi flap, brief network hiccup) before we force a
    // restart. Without this, every micro-stutter would tear down the call.
    if (state === 'disconnected' && everConnected && !restartTimer) {
      restartTimer = setTimeout(() => {
        restartTimer = null;
        if (pc.connectionState !== 'disconnected') return;
        try { pc.restartIce(); } catch (e) { /* noop */ }
        renegotiate();
      }, 4000);
    }
    // Hard failure → ICE restart is the last shot.
    if (state === 'failed') {
      try { pc.restartIce(); } catch (e) { /* noop */ }
      renegotiate();
    }
  };

  // Listen for the mobile side's ICE candidates and push them into the PC.
  // Guard against candidates that arrive after the connection is closed
  // (addIceCandidate throws InvalidStateError on closed connections).
  signaling.onMobileIce((candidate) => {
    if (!candidate) return;
    if (pc.connectionState === 'closed') return;
    pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => { /* noop */ });
  });

  // Accept the mobile-side answer for both the original offer and any
  // ICE-restart re-offers. The previous `signalingState === 'stable'` guard
  // dropped restart answers; instead we only ignore the duplicate of the
  // first answer (stable + never-connected = duplicate).
  signaling.onAnswer(async (answer) => {
    if (!answer || !answer.sdp) return;
    if (pc.signalingState === 'stable' && !everConnected) return;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (err) { /* noop */ }
  });

  const start = async () => {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await signaling.writeOffer({ sdp: offer.sdp, type: offer.type });
  };

  const stop = () => {
    if (restartTimer) { clearTimeout(restartTimer); restartTimer = null; }
    try { pc.close(); } catch (e) { /* noop */ }
  };

  return { pc, start, stop };
};

// ── Mobile side (callee, sendonly) ──────────────────────────────────────
// Receives the offer, attaches local camera + mic, creates the answer.
export const createMobileClient = ({
  signaling,
  localStream,
  onConnectionStateChange,
}) => {
  const pc = new RTCPeerConnection(PC_CONFIG);

  // Add local tracks — gets transmitted to the desktop
  const senders = {};
  localStream.getTracks().forEach((track) => {
    senders[track.kind] = pc.addTrack(track, localStream);
  });

  pc.onicecandidate = (e) => {
    if (e.candidate) {
      try { signaling.sendMobileIce(e.candidate.toJSON()); } catch (err) { /* noop */ }
    }
  };

  pc.onconnectionstatechange = () => {
    if (typeof onConnectionStateChange === 'function') {
      onConnectionStateChange(pc.connectionState);
    }
  };

  // Filter ICE candidates that arrive after the connection has been closed
  // (callbacks can fire one more time after stop() on some browsers).
  signaling.onDesktopIce((candidate) => {
    if (!candidate) return;
    if (pc.connectionState === 'closed') return;
    pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => { /* noop */ });
  });

  // Accept the original offer AND any ICE-restart re-offers from the
  // desktop side. The previous one-shot `offerHandled` guard rejected
  // restart attempts, leaving a stuck call when the desktop tried to
  // recover from a transient drop.
  let lastOfferSdp = null;
  signaling.onOffer(async (offer) => {
    if (!offer || !offer.sdp) return;
    if (offer.sdp === lastOfferSdp) return; // exact duplicate of last write
    lastOfferSdp = offer.sdp;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await signaling.writeAnswer({ sdp: answer.sdp, type: answer.type });
    } catch (err) { /* noop */ }
  });

  // Replace the video track without renegotiation (front <-> back camera).
  const replaceVideoTrack = async (newTrack) => {
    if (senders.video && newTrack) {
      try { await senders.video.replaceTrack(newTrack); } catch (e) { /* noop */ }
    }
  };

  // Toggle mic mute by enabling/disabling the local audio track
  const setMicMuted = (muted) => {
    if (senders.audio && senders.audio.track) {
      senders.audio.track.enabled = !muted;
    }
  };

  const stop = () => {
    try { pc.close(); } catch (e) { /* noop */ }
  };

  return {
    pc, replaceVideoTrack, setMicMuted, stop,
  };
};
